import { gql } from "lib/apolloClient";
import { studentsAlertFragment } from "modules/CommonFragments";
import {
  userFragment,
  messageFragment,
  manageMembersFragments,
} from "./MessagingFragments";

export const getChatDetailsQuery = gql`
  query getChatDetails($id: ID!, $userId: ID, $organizationId: ID) {
    node(id: $id, type: CHAT) {
      ... on Chat {
        id
        label
        description
        bannerMessage
        profileImageData {
          url
          icon
        }
        isArchived
        archivedBy {
          id
          type
        }
        unreadMessageCount
        isMuted
        isMembersListVisible
        memberListHiddenRoles
        type
        viewOnlyRoles
        viewOnlyUsers {
          id
        }
        lastActiveAt
        totalUsersCount
        isPersonalChatPaid
        createdBy {
          id
          firstName
          lastName
          middleName
          type
        }
        createdAt
        lastReadMessage {
          id
          cursor
        }
        users {
          ...userDetailItem
          ... on Student {
            alertTypes {
              ...alertTypeFields
            }
          }
        }
        isConnectEnabledForReceiver
        personalChatReceiverUser(userId: $userId) {
          ...chatUserV2DetailsItem
          ... on Student {
            alertTypes {
              ...alertTypeFields
            }
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
  ${userFragment.chatUserV2DetailsItem}
  ${studentsAlertFragment}
`;

export const getChatConversationsQuery = gql`
  query getChatConversations(
    $id: ID!
    $organizationId: ID!
    $input: MessageFeedInput
  ) {
    node(id: $id, type: CHAT) {
      ... on Chat {
        id
        label
        isActive
        unreadMessageCount
        isMuted
        messagesV2(input: $input) {
          totalCount
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
          edges {
            cursor
            node {
              id
              ...messageV2ItemDetail
            }
          }
        }
      }
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const getChatMessagesForPreviewQuery = gql`
  query getChatConversations(
    $id: ID!
    $organizationId: ID!
    $input: MessageFeedInput
  ) {
    node(id: $id, type: CHAT) {
      ... on Chat {
        id
        type
        lastReadMessage {
          id
          cursor
        }
        messagesV2(input: $input) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              id
              ...messageV2ItemDetail
            }
          }
        }
      }
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const getLatestUnreadStateQuery = gql`
  query getLatestUnreadState($id: ID!) {
    node(id: $id, type: CHAT) {
      ... on Chat {
        id
        unreadMessageCount
        lastReadMessage {
          id
          cursor
        }
      }
    }
  }
`;

export const getMessageV2UsersQuery = gql`
  query getMessageV2Users(
    $organizationId: ID!
    $input: MessageV2UserInput
    $includeTotalCount: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        messageV2Users(input: $input) {
          totalCount(input: $input) @include(if: $includeTotalCount)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userDetailItemWithCurriculum
              ... on Student {
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${userFragment.userDetailItemWithCurriculum}
  ${studentsAlertFragment}
`;

export const getCourseUsersQuery = gql`
  query getCourseUsers(
    $id: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              students {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
              collaborator {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
              familyMembers {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationCourseUsersQuery = gql`
  query getCourseUsers(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              students {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
              collaborator {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
              familyMembers {
                edges {
                  node {
                    id
                    type
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradeUsersQuery = gql`
  query getGradeUsers($organizationId: ID!, $filters: GradeFilterInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          staffCount
          studentCount
          familyMemberCount
        }
      }
    }
  }
`;

export const getGradeUsersByTypeQuery = gql`
  query getGradeUsersByType(
    $organizationId: ID!
    $gradeIds: [ID!]
    $filters: GradeFilterInput
    $isStudents: Boolean!
    $isStaffMembers: Boolean!
    $isParents: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(ids: $gradeIds, filters: $filters) {
          id
          courses {
            edges {
              node {
                id
                students @include(if: $isStudents) {
                  edges {
                    node {
                      id
                      type
                    }
                  }
                }
                collaborator @include(if: $isStaffMembers) {
                  edges {
                    node {
                      id
                      type
                    }
                  }
                }
                familyMembers @include(if: $isParents) {
                  edges {
                    node {
                      id
                      type
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getMessageQuery = gql`
  query getMessage($id: ID!, $organizationId: ID!) {
    node(id: $id, type: MESSAGEV2) {
      ...messageV2ItemDetail
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const getMessageReactionsQuery = gql`
  query getMessageReactions($id: ID!) {
    node(id: $id, type: MESSAGEV2) {
      ...messageWithReactionsItem
    }
  }
  ${messageFragment.messageWithReactionsItem}
`;

export const getMessageReadReceiptCountQuery = gql`
  query messageReadReceipt(
    $id: ID!
    $readInput: ReadReceiptInput
    $unreadInput: ReadReceiptInput
  ) {
    node(id: $id, type: MESSAGEV2) {
      ... on MessageV2 {
        id
        readReceipt(input: $readInput) {
          readCount: totalCount(input: $readInput)
          unreadCount: totalCount(input: $unreadInput)
        }
      }
    }
  }
`;

export const getMessageReadReceiptUserQuery = gql`
  query messageReadReceipt(
    $id: ID!
    $organizationId: ID!
    $input: ReadReceiptInput
  ) {
    node(id: $id, type: MESSAGEV2) {
      ... on MessageV2 {
        id
        readReceipt(input: $input) {
          totalCount(input: $input)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userDetailItem
            }
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

export const getMessageReadStateQuery = gql`
  query getMessageReadState($id: ID!) {
    node(id: $id, type: MESSAGEV2) {
      ... on MessageV2 {
        id
        isRead
      }
    }
  }
`;

export const getChatMessagesReadStateQuery = gql`
  query getChatMessagesReadState(
    $id: ID!
    $messageFeedInput: MessageFeedInput
  ) {
    node(id: $id, type: CHAT) {
      ... on Chat {
        id
        messagesV2(input: $messageFeedInput) {
          edges {
            node {
              id
              isRead
            }
          }
        }
      }
    }
  }
`;

// query for add members v3

export const getCourseUsersQueryV2 = gql`
  query getCourseUsers(
    $id: ID!
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              students {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getOrganizationCourseUsersQueryV2 = gql`
  query getCourseUsers(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              students {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              collaborator {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
              familyMembers {
                edges {
                  node {
                    ...userDetailItem
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getGradeUsersQueryV2 = gql`
  query getGradeUsers($organizationId: ID!, $filters: GradeFilterInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          allStaffs {
            ...userDetailItem
          }
          allStudents {
            ...userDetailItem
          }
          allParents {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getYearGroupUsersQuery = gql`
  query getYearGroupUsers($organizationId: ID!, $filters: YearGroupFilter) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          id
          name
          allStudents {
            ...userDetailItem
          }
          allStaffs {
            ...userDetailItem
          }
          allParents {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getMessageV2UsersWithStudentParentQuery = gql`
  query getMessageV2Users(
    $organizationId: ID!
    $input: MessageV2UserInput
    $getStudentFamilyMembers: Boolean = true
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        messageV2Users(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userItem
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.userItem}
`;

export const getCourseUsersQueryV3 = gql`
  query getCourseUsersV3(
    $id: ID!
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
    $includeStudents: Boolean = true
    $includeStaff: Boolean = true
    $includeParents: Boolean = true
    $includeCurriculumPrograms: Boolean = false
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              grades {
                id
                name
              }
              curriculumProgram @include(if: $includeCurriculumPrograms) {
                id
                label
              }
              students @include(if: $includeStudents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              collaborator @include(if: $includeStaff) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              familyMembers @include(if: $includeParents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;

export const getOrganizationCourseUsersQueryV3 = gql`
  query getOrganizationCourseUsersV3(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
    $includeStudents: Boolean = true
    $includeStaff: Boolean = true
    $includeParents: Boolean = true
    $includeCurriculumPrograms: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              grades {
                id
                name
              }
              curriculumProgram @include(if: $includeCurriculumPrograms) {
                id
                label
              }
              students @include(if: $includeStudents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              collaborator @include(if: $includeStaff) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
              familyMembers @include(if: $includeParents) {
                edges {
                  node {
                    ...userDetailItemV2
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;

export const getGradeUsersQueryV3 = gql`
  query getGradeUsersV3(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: OrganizationGradeV2FilterInput
    $includeStudents: Boolean = true
    $includeStaff: Boolean = true
    $includeParents: Boolean = true
    $includeCurriculumPrograms: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        gradesV2(input: { first: $first, after: $after, filter: $filters }) {
          totalCount(input: { filter: $filters })
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              name
              curriculumProgram @include(if: $includeCurriculumPrograms) {
                id
                label
              }
              allStaffs @include(if: $includeStaff) {
                ...userDetailItemV2
              }
              allStudents @include(if: $includeStudents) {
                ...userDetailItemV2
              }
              allParents @include(if: $includeParents) {
                ...userDetailItemV2
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItemV2}
`;

export const getMessageV2UsersWithStudentParentQueryV3 = gql`
  query getMessageV2UsersV3(
    $organizationId: ID!
    $input: MessageV2UserInput
    $getStudentFamilyMembers: Boolean = true
    $includeCurriculumPrograms: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        messageV2Users(input: $input) {
          totalCount(input: $input)
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userItemV2
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.userItemV2}
`;

export const getMessagingStudentsWithFamilyMemberQuery = gql`
  query getMessagingStudentsWithFamilyMember(
    $organizationId: ID!
    $input: MessageV2UserInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        messageV2Users(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Student {
                id
                profileImage
                firstName
                lastName
                middleName
                preferredName
                pronouns
                prefix
                suffix
                type
                yearGroup {
                  id
                  grade {
                    id
                    name
                  }
                  curriculumProgram {
                    id
                    label
                  }
                }
                familyMembers {
                  id
                }
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

// for chat to channel

export const getUserGroupChannelCountQuery = gql`
  query getUserGroupChannelCount(
    $organizationId: ID!
    $input: ChatFeedInput
    $messageInput: MessageFeedInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        chats(input: $input) {
          totalCount(input: $input)
        }
        messages(input: $messageInput) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getUserGroupChannelsQuery = gql`
  query getUserGroupChannels($organizationId: ID!, $input: ChatFeedInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        chats(input: $input) {
          pageInfo {
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              label
              type
              profileImageData {
                url
                icon
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserAccessPointDetailsQuery = gql`
  query getUserAccessPointDetails(
    $id: ID!
    $userType: ENTITY_TYPE_ENUM!
    $skipGradeFetch: Boolean = false
    $skipClassesFetch: Boolean = false
    $skipFamilyMembersFetch: Boolean = false
    $skipDisplayRoleFetch: Boolean = false
    $skipCurriculumProgramFetch: Boolean = false
    $academicYearId: ID
    $organizationId: ID
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        firstName
        middleName
        lastName
        type
        email
        profileImage
        isArchived
        displayRole @skip(if: $skipDisplayRoleFetch)
        preferredName
        pronouns
        prefix
        suffix
        courses(orderBy: TITLE, orderByDirection: ASC)
          @skip(if: $skipClassesFetch) {
          id
          title
        }
      }
      ... on Student {
        firstName
        middleName
        lastName
        preferredName
        pronouns
        prefix
        suffix
        type
        email
        profileImage
        isArchived
        allGrades @skip(if: $skipGradeFetch) {
          id
          name
        }
        familyMembers @skip(if: $skipFamilyMembersFetch) {
          id
          firstName
          lastName
          middleName
          profileImage
          preferredName
          pronouns
          prefix
          suffix
        }
        curriculumPrograms(filters: { academicYearId: $academicYearId })
          @skip(if: $skipCurriculumProgramFetch) {
          id
        }
      }
      ... on FamilyMember {
        firstName
        middleName
        lastName
        type
        profileImage
        preferredName
        pronouns
        prefix
        suffix
        children(organizationId: $organizationId) {
          id
          firstName
          lastName
          middleName
          preferredName
          pronouns
          prefix
          suffix
          profileImage
          isFamilyChannelAllowed
          familyMembers {
            id
          }
          yearGroup {
            id
            grade {
              id
              name
            }
          }
          alertTypes {
            ...alertTypeFields
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

export const getSearchedMembersInChatQuery = gql`
  query getSearchedMembersInChat(
    $chatId: ID!
    $input: UserV2Input
    $organizationId: ID!
  ) {
    node(id: $chatId, type: CHAT) {
      ... on Chat {
        id
        usersV2(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userDetailsItemWithBlockStatus
              ... on Student {
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${userFragment.userDetailsItemWithBlockStatus}
  ${studentsAlertFragment}
`;

export const getMultiUserAccessPointDetailsQuery = gql`
  query getMultiUserAccessPointDetails(
    $organizationId: ID!
    $input: MessageV2UserInput
    $academicYearId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        messageV2Users(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ... on Staff {
                firstName
                middleName
                lastName
                type
                email
                profileImage
                isArchived
                displayRole
                preferredName
                pronouns
                prefix
                suffix
                courses(orderBy: TITLE, orderByDirection: ASC) {
                  id
                  title
                }
              }
              ... on Student {
                firstName
                middleName
                lastName
                preferredName
                pronouns
                prefix
                suffix
                type
                email
                profileImage
                isArchived
                allGrades {
                  id
                  name
                }
                familyMembers {
                  id
                  firstName
                  lastName
                  middleName
                  profileImage
                  preferredName
                  pronouns
                  prefix
                  suffix
                }
                curriculumPrograms(
                  filters: { academicYearId: $academicYearId }
                ) {
                  id
                }
              }
              ... on FamilyMember {
                firstName
                middleName
                lastName
                type
                profileImage
                preferredName
                pronouns
                prefix
                suffix
                children(organizationId: $organizationId) {
                  id
                  firstName
                  lastName
                  middleName
                  preferredName
                  pronouns
                  prefix
                  suffix
                  profileImage
                  isFamilyChannelAllowed
                  familyMembers {
                    id
                  }
                  yearGroup {
                    id
                    grade {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStaffRnpFilterCoursesCountQuery = gql`
  query getStaffRnpFilterCoursesCount($userId: ID!, $filters: CourseFilter) {
    node(id: $userId, type: STAFF) {
      id
      ... on Staff {
        coursesV2(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getDepartmentsQuery = gql`
  query getDepartments($organizationId: ID!, $input: ConnectDepartmentInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        connectDepartments(input: $input) {
          pageInfo {
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              name
              description
              profileImage
              users {
                totalCount
              }
            }
          }
        }
      }
    }
  }
`;

export const getDepartmentQuery = gql`
  query getDepartment($id: ID!) {
    node(id: $id, type: CONNECT_DEPARTMENT) {
      ... on ConnectDepartment {
        id
        name
        users {
          totalCount
        }
      }
    }
  }
`;

export const getDepartmentUsersQuery = gql`
  query getDepartmentUsers(
    $id: ID!
    $input: ConnectDepartmentUserInput
    $organizationId: ID
  ) {
    node(id: $id, type: CONNECT_DEPARTMENT) {
      ... on ConnectDepartment {
        id
        users(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userDetailItem
            }
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

export const familyMembersFromStudentsQuery = gql`
  query getMessageV2Users(
    $organizationId: ID!
    $filter: FamilyMembersFilterInput
    $getStudentFamilyMembers: Boolean = false
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        familyMembers(filters: $filter) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...userItem
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.userItem}
`;

// poll query

export const getPollOptionUsersQuery = gql`
  query getPollOptionUsers($id: ID!, $organizationId: ID!) {
    node(id: $id, type: MESSAGEV2_POLL) {
      ... on MessageV2Poll {
        id
        options {
          id
          label
          metadata
          selectedByUsers {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $id: ID!
    $first: Int
    $after: String
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              firstName
              lastName
              middleName
              profileImage
              type
            }
          }
        }
      }
    }
  }
`;

export const getInteractionListQuery = gql`
  query getInteractionList($id: ID!, $input: MessageV2InteractionInput!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        messageV2Interactions(input: $input) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            totalQuestions
            node {
              id
              type
              ... on MessageV2Form {
                title
                createdBy {
                  id
                  firstName
                  lastName
                  middleName
                  type
                }
              }
              ... on MessageV2Poll {
                question
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCourseIdsQuery = gql`
  query getUserCourseIds($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        allCourses {
          id
        }
      }
      ... on Student {
        id
        allCourses {
          id
        }
      }
      ... on FamilyMember {
        id
        children {
          id
          allCourses {
            id
          }
        }
      }
    }
  }
`;
